<template>
  <div>
    <!-- Page header -->
    <v-row
      class="mb-0"
      align="center"
    >
      <v-col
        cols="12"
        sm="6"
        class="pb-2"
      >
        <p class="text-2xl font-weight-bold mb-0">
          Relatório de Serviços
        </p>
        <p class="text-xs text--secondary mb-0 mt-1">
          <v-icon
            size="13"
            class="mr-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Atualiza automaticamente
        </p>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        class="d-flex justify-end pb-2"
      >
        <v-btn-toggle
          v-model="selectedInterval"
          mandatory
          dense
          rounded
          color="primary"
          class="interval-toggle"
        >
          <v-btn
            v-for="interval in intervals"
            :key="interval.value"
            :value="interval.value"
            small
            @click="getServiceDetail({ interval: interval.value })"
          >
            {{ interval.label }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Stat cards -->
    <v-row>
      <v-col
        v-for="service in serviceInfo"
        :key="service.title"
        cols="12"
        md="2"
        sm="4"
      >
        <v-card
          outlined
          class="stat-card h-full"
        >
          <div
            class="stat-card-accent"
            :style="{ background: resolveIconVariation(service.title).accentColor }"
          />
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-avatar
                size="38"
                :color="resolveIconVariation(service.title).color"
                rounded
                class="elevation-1"
              >
                <v-icon
                  dark
                  color="white"
                  size="22"
                >
                  {{ resolveIconVariation(service.title).icon }}
                </v-icon>
              </v-avatar>
              <p class="text-xs text--secondary mb-0 ms-2 font-weight-medium">
                {{ service.title }}
              </p>
            </div>
            <h2
              class="stat-value font-weight-bold mb-1"
              :style="{ color: resolveIconVariation(service.title).accentColor }"
            >
              {{ service.total }}
            </h2>
            <a
              class="text-xs"
              :class="service.total > 0 ? 'primary--text' : 'text--disabled'"
              @click="service.total > 0 ? openServiceDetailDialog(service.type) : null"
            >
              Ver detalhes →
            </a>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="3"
        sm="6"
      >
        <v-card
          outlined
          class="stat-card h-full"
        >
          <div
            class="stat-card-accent"
            style="background: #0091EA"
          />
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-avatar
                size="38"
                color="info"
                rounded
                class="elevation-1"
              >
                <v-icon
                  dark
                  color="white"
                  size="22"
                >
                  {{ resolveIconVariation('Carteira').icon }}
                </v-icon>
              </v-avatar>
              <p class="text-xs text--secondary mb-0 ms-2 font-weight-medium">
                Carteira
              </p>
            </div>
            <h2
              class="stat-value font-weight-bold mb-1"
              style="color: #0091EA"
            >
              R$ {{ availableLimit }}
            </h2>
            <a
              class="text-xs primary--text"
              href="/account/wallet-extract"
              target="_blank"
            >
              Ver extrato →
            </a>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="3"
        sm="6"
      >
        <v-card
          outlined
          class="stat-card h-full"
        >
          <div
            class="stat-card-accent"
            style="background: #7C4DFF"
          />
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-avatar
                size="38"
                color="deep-purple"
                rounded
                class="elevation-1"
              >
                <v-icon
                  dark
                  color="white"
                  size="22"
                >
                  {{ resolveIconVariation('Token').icon }}
                </v-icon>
              </v-avatar>
              <p class="text-xs text--secondary mb-0 ms-2 font-weight-medium">
                Token API
              </p>
            </div>
            <v-text-field
              :value="'*'.repeat(20)"
              :append-icon="icons.mdiContentCopy"
              outlined
              hide-details
              dense
              type="text"
              readonly
              @click:append="copyTokenApi"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Campaigns table -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="pa-0">
            <div class="table-scroll-wrapper">
              <v-data-table
                :headers="headers"
                :items="campaigns"
                :items-per-page="10"
                item-key="id"
                disable-sort
                class="services-table"
              >
                <!-- eslint-disable vue/valid-v-for -->
                <template
                  v-for="col in headersWithFilter"
                  v-slot:[`header.${col}`]="{ header }"
                >
                  {{ header.text }}
                  <v-btn
                    color="white"
                    icon
                    x-small
                    class="ml-1 opacity-60"
                    @click="dialogFilter = true"
                  >
                    <v-icon size="14">
                      {{ icons.mdiFilterMenu }}
                    </v-icon>
                  </v-btn>
                </template>
                <!-- eslint-enable vue/valid-v-for -->

                <template #[`item.action`]="{ item }">
                  <v-btn
                    color="info"
                    title="Visualizar campanha"
                    icon
                    small
                    @click="openCampaignDetail(item)"
                  >
                    <v-icon size="20">
                      {{ icons.mdiEye }}
                    </v-icon>
                  </v-btn>
                </template>

                <template #[`item.id`]="{ item }">
                  <span class="text--secondary text-caption">{{ item.id }}</span>
                </template>

                <template #[`item.name`]="{ item }">
                  <div
                    class="text-truncate font-weight-medium"
                    style="max-width: 200px;"
                  >
                    {{ item.name }}
                  </div>
                </template>

                <template #[`item.serviceName`]="{ item }">
                  <div
                    class="d-flex align-center service-label"
                    :style="{ color: resolveServiceMeta(item.serviceName).color }"
                  >
                    <v-icon
                      size="14"
                      class="me-1"
                      :color="resolveServiceMeta(item.serviceName).color"
                    >
                      {{ resolveServiceMeta(item.serviceName).icon }}
                    </v-icon>
                    <span class="font-weight-medium">{{ item.serviceName }}</span>
                  </div>
                </template>

                <template #[`item.internalStatus`]="{ item }">
                  <v-chip
                    :color="resolveCampaignStatus(item.internalStatus).color"
                    small
                    label
                  >
                    <span class="white--text text-caption font-weight-medium">
                      {{ resolveCampaignStatus(item.internalStatus).label }}
                    </span>
                  </v-chip>
                </template>

                <template #[`item.userName`]="{ item }">
                  <div
                    class="text-truncate"
                    style="max-width: 180px;"
                  >
                    {{ item.userName }}
                  </div>
                </template>

                <template #[`item.sendDate`]="{ item }">
                  <span class="text-no-wrap text-caption">
                    {{ $day(item.sendDate).format('DD/MM/YYYY') }}
                    <span class="text--secondary mx-1">·</span>
                    {{ $day(item.sendDate).format('HH:mm') }}
                  </span>
                </template>

                <template #[`item.value`]="{ item }">
                  <span class="font-weight-medium">{{ $money(item.value, 3) }}</span>
                </template>

                <template #[`item.quantity`]="{ item }">
                  <span class="font-weight-medium">{{ $money(item.quantity, 0) }}</span>
                </template>

                <template #[`item.validQuantity`]="{ item }">
                  {{ $money(item.validQuantity, 0) }}
                </template>

                <template #[`item.invalidQuantity`]="{ item }">
                  {{ $money(item.invalidQuantity, 0) }}
                </template>

                <template #[`item.affiliateId`]="{ item }">
                  <span class="text--secondary">{{ item.affiliateId }}</span>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <filter-service
      :status="dialogFilter"
      :logged-in-user-is-admin="loggedInUserIsAdmin"
      @filter="filterCampaign"
      @close="dialogFilter = false"
    />

    <service-detail
      v-if="dialogServiceDetail"
      :status="dialogServiceDetail"
      :type="serviceDetailType"
      :campaign-id="serviceDetailCampaignId"
      :interval="selectedInterval"
      :details="serviceDetails"
      @close="dialogServiceDetail = false"
    />

    <campaign-detail
      v-if="dialogCampaignDetail"
      :status="dialogCampaignDetail"
      :campaign="selectedCampaign"
      @close="dialogCampaignDetail = false"
      @cancel="cancelSelectedCampaign"
      @analytic="openServiceDetailDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { ref } from 'vue'

import {
  mdiAccountCash,
  mdiCalendar,
  mdiCheckAll,
  mdiClock,
  mdiEye,
  mdiFilterMenu,
  mdiMessageTextOutline,
  mdiPhonePlusOutline,
  mdiContentCopy,
  mdiApi,
  mdiFilter,
  mdiRefresh,
} from '@mdi/js'
import { ROLE_TYPE } from '@/store/auth/mutations'
import { campaignStatus } from '@/utils/enum'
import FilterService from './filter.vue'
import CampaignDetail from './campaign-detail.vue'
import ServiceDetail from './service-detail.vue'

export default {
  components: {
    FilterService,
    CampaignDetail,
    ServiceDetail,
  },

  setup() {
    const dialogFilter = ref(false)
    const dialogCampaignDetail = ref(false)
    const dialogServiceDetail = ref(false)
    const serviceDetails = ref([])
    const serviceDetailType = ref(null)
    const availableLimit = ref('...')
    const tokenApi = localStorage.getItem('tokenApi') || ''
    const selectedCampaign = ref({})

    const intervalDialog = ref(false)
    const selectedInterval = ref('day')
    const intervals = [
      {
        value: 'day',
        label: 'Hoje',
        description: 'Resumo de hoje',
      },
      {
        value: 'week',
        label: 'Semana',
        description: 'Resumo dos últimos 7 dias',
      },
      {
        value: 'month',
        label: 'Mês',
        description: 'Resumo dos últimos 30 dias',
      },
    ]

    const serviceInfo = ref([
      {
        title: 'SMS',
        total: '...',
        type: 'sms',
      },
      {
        title: 'Voz',
        total: '...',
        type: 'voice',
      },
      {
        title: 'Higienização',
        total: '...',
        type: 'sanitizer',
      },
    ])

    const resolveIconVariation = data => {
      if (data === 'SMS') return { icon: mdiMessageTextOutline, color: 'primary', accentColor: '#06F11A' }
      if (data === 'Voz') return { icon: mdiPhonePlusOutline, color: 'warning', accentColor: '#FFB400' }
      if (data === 'Higienização') return { icon: mdiCheckAll, color: 'success', accentColor: '#00C853' }
      if (data === 'Carteira') return { icon: mdiAccountCash, color: 'info', accentColor: '#0091EA' }
      if (data === 'Token') return { icon: mdiApi, color: 'deep-purple', accentColor: '#7C4DFF' }

      return { icon: mdiPhonePlusOutline, color: 'primary', accentColor: '#06F11A' }
    }

    const resolveServiceMeta = name => {
      if (!name) return { color: '#0091EA', icon: mdiMessageTextOutline }
      const n = name.toLowerCase()
      if (n.includes('sms')) return { color: '#06F11A', icon: mdiMessageTextOutline }
      if (n.includes('voz') || n.includes('voice')) return { color: '#FFB400', icon: mdiPhonePlusOutline }
      if (n.includes('higien') || n.includes('sanit')) return { color: '#00C853', icon: mdiCheckAll }

      return { color: '#0091EA', icon: mdiMessageTextOutline }
    }

    // kept for backwards compat with existing call sites
    const resolveServiceColor = name => resolveServiceMeta(name).color
    const resolveServiceIcon = name => resolveServiceMeta(name).icon

    const resolveCampaignStatus = value => {
      const internalStatus = campaignStatus.find(item => item.value === value)

      if (internalStatus) {
        return internalStatus
      }

      return { color: 'error', label: 'Não definido' }
    }

    const headers = [
      {
        text: 'Ações', value: 'action', align: 'center', width: '100px',
      },
      {
        text: 'ID', value: 'id', align: 'center', width: '80px',
      },
      { text: 'Campanha', value: 'name', width: '220px' },
      { text: 'Status', value: 'internalStatus', width: '140px' },
      { text: 'Serviço', value: 'serviceName', width: '160px' },
      { text: 'Cliente', value: 'userName', width: '180px' },
    ]

    const loggedInUserRole = Number(Buffer.from(localStorage.getItem('role'), 'base64'))
    const loggedInUserIsAdmin = [ROLE_TYPE.admin].includes(loggedInUserRole)

    headers.push(...[
      { text: 'Data Envio', value: 'sendDate', width: '160px' },
      {
        text: 'Preço (R$)', value: 'value', align: 'right', width: '120px',
      },
      {
        text: 'Quantidade', value: 'quantity', align: 'right', width: '120px',
      },
    ])

    if (loggedInUserIsAdmin) {
      headers.push({
        text: 'ID Afiliado', value: 'affiliateId', width: '120px', align: 'right',
      })
    }

    const headersWithFilter = [
      'id',
      'name',
      'internalStatus',
      'serviceName',
      'userName',
      'sendDate',
    ]

    return {
      dialogFilter,
      dialogCampaignDetail,
      dialogServiceDetail,
      serviceDetails,
      serviceDetailType,
      availableLimit,
      resolveIconVariation,
      resolveCampaignStatus,
      resolveServiceColor,
      resolveServiceIcon,
      resolveServiceMeta,
      headers,
      headersWithFilter,
      loggedInUserIsAdmin,
      tokenApi,
      selectedCampaign,

      icons: {
        mdiAccountCash,
        mdiCalendar,
        mdiCheckAll,
        mdiClock,
        mdiEye,
        mdiFilterMenu,
        mdiMessageTextOutline,
        mdiPhonePlusOutline,
        mdiContentCopy,
        mdiApi,
        mdiFilter,
        mdiRefresh,
      },

      serviceInfo,

      intervalDialog,
      selectedInterval,
      intervals,
    }
  },

  computed: {
    ...mapState('reportServices', {
      campaigns: state => state.rows,
    }),
  },

  async created() {
    await this.fetchReportServices()
    await this.getUserExtract()

    await this.getServiceDetail()
  },

  methods: {
    ...mapActions('reportServices', [
      'find',
      'cancelCampaign',
    ]),

    ...mapActions('walletExtract', {
      userExtract: 'find',
    }),

    ...mapActions('reportDetail', [
      'getDetails',
    ]),

    copyTokenApi() {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.tokenApi)
          .then(() => {
            this.$notify('Token API copiado para a área de transferência', 'success')
          })
          .catch(() => {
            this.fallbackCopyTokenApi()
          })
      } else {
        this.fallbackCopyTokenApi()
      }
    },

    fallbackCopyTokenApi() {
      const textArea = document.createElement('textarea')
      textArea.value = this.tokenApi
      textArea.style.position = 'fixed'
      textArea.style.top = '-9999px'
      textArea.style.left = '-9999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        const success = document.execCommand('copy')
        if (success) {
          this.$notify('Token API copiado para a área de transferência', 'success')
        } else {
          this.$notify('Erro ao copiar Token API', 'error')
        }
      } catch {
        this.$notify('Erro ao copiar Token API', 'error')
      } finally {
        document.body.removeChild(textArea)
      }
    },

    openServiceDetailDialog(type, campaignId = null) {
      this.serviceDetailType = type
      this.serviceDetailCampaignId = campaignId
      this.dialogServiceDetail = true
    },

    openCampaignDetail(campaign) {
      this.dialogCampaignDetail = true
      this.selectedCampaign = campaign
    },

    async getServiceDetail(params = {}) {
      const details = await this.getDetails(params)
      const serviceTotals = new Map([
        ['SMS', 0],
        ['Higienizador', 0],
        ['Voz', 0],
      ])

      this.serviceDetails = details.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
      }))

      this.serviceDetails.forEach(item => {
        // eslint-disable-next-line no-restricted-syntax
        for (const key of serviceTotals.keys()) {
          if (item.name.includes(key)) {
            serviceTotals.set(key, serviceTotals.get(key) + Number(item.quantity))
          }
        }
      })

      this.serviceInfo[0].total = serviceTotals.get('SMS')
      this.serviceInfo[1].total = serviceTotals.get('Voz')
      this.serviceInfo[2].total = serviceTotals.get('Higienizador')
    },

    async fetchReportServices(params = {}) {
      await this.find(params)
    },

    async filterCampaign(params) {
      await this.fetchReportServices(params)
    },

    async getUserExtract() {
      const extract = await this.userExtract()
      this.availableLimit = this.$money(String(extract.total.availableLimit))
    },

    async cancelSelectedCampaign(campaignId) {
      await this.cancelCampaign(campaignId)
      await this.filterCampaign()

      this.dialogCampaignDetail = false
    },
  },
}
</script>

<style lang="scss" scoped>
.stat-card {
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18) !important;
  }
}

.stat-value {
  font-size: 1.75rem;
  line-height: 1.1;
  white-space: nowrap;
}

.stat-card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 14px 14px 0 0;
}

.h-full {
  height: 100%;
}

.interval-toggle {
  .v-btn {
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.8rem;
  }
}

.opacity-60 {
  opacity: 0.6;
}

.service-label {
  font-size: 0.825rem;
  white-space: nowrap;
}

::v-deep .services-table {
  .v-chip.v-chip--label {
    border-radius: 3px !important;
  }
}

.table-scroll-wrapper {
  overflow-x: auto;

  .services-table {
    min-width: 1000px;
  }
}

::v-deep .services-table {
  .v-data-table__wrapper {
    tbody tr {
      transition: background 0.15s ease;

      &:hover {
        background: rgba(6, 241, 26, 0.04) !important;
      }
    }
  }

  .v-data-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}
</style>
